import React, { useEffect, useState } from "react";
import { ProColumns } from "@ant-design/pro-table/es/typing";
import clsx from "clsx";
import dayjs from "dayjs";
import { Badge, Button, Dropdown, MenuProps, message, Select, Tag } from "antd";
import {
  DownOutlined,
  ExportOutlined,
  HomeOutlined,
  PlusCircleOutlined,
  UploadOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
// import Table from "../../Common/Table";
import { useTranslation } from "react-i18next";
import Table from "../../../../../Common/Table";
import { IconCrown, IconExport } from "../../../../../Common/Icon";
import SelectExistingUserModal from "./SelectUserModal";
import {
  UserTableRow,
  useUsersTableGet,
} from "../../../../../../hooks/api/users";
import { getSorterParams, queryFilterParams } from "../../../../../../utils";
import { RequestData } from "@ant-design/pro-components";
import { SortOrder } from "antd/es/table/interface";
import { useUsersExportDownload } from "../../../../../../utils/exportMembers";
import CreateModal from "../../../../Users/Create";

interface MembersTabProps {
  activeTab?: string;
}

// export interface MemberTableRow {
//   key: string;
//   id: string;
//   fullName: string;
//   phone: string;
//   email: string;
//   address: string;
//   subscriptionStatus: "active" | "cancelled" | "expired" | "payment_failed";
//   subscriptionType: "monthly" | "yearly";
// }

export const SubscriptionStatusDisplay = {
  active: { text: "Active", color: "green" },
  trialing: { text: "Trialing", color: "blue" },
  incomplete: { text: "Incomplete", color: "orange" },
  incomplete_expired: { text: "Incomplete expired", color: "red" },
  past_due: { text: "Past due", color: "volcano" },
  canceled: { text: "Canceled", color: "red" },
  cancelled: { text: "Cancelled", color: "red" },
  unpaid: { text: "Unpaid", color: "red" },
  paused: { text: "Paused", color: "gold" },
  payment_failed: { text: "Payment failed", color: "orange" },
} as const;

const SubscriptionTypeDisplay = {
  monthly: { text: "Monthly", color: "green" },
  yearly: { text: "Yearly", color: "orange" },
} as const;

export const columns = (vaadId?: string): ProColumns<UserTableRow>[] => [
  {
    title: "Full Name",
    dataIndex: "fullName",
    key: "fullName",
    sorter: true,
    hideInSearch: true,
    render: (_, record) => (
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {record.role === "leader" ? <IconCrown /> : null} {record.fullName}
      </div>
    ),
    // render: (_, record) => (
    //   <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    //     <div
    //       style={{
    //         width: 32,
    //         height: 32,
    //         borderRadius: "50%",
    //         background: "#f0f0f0",
    //         display: "flex",
    //         alignItems: "center",
    //         justifyContent: "center",
    //       }}
    //     >
    //       <UserOutlined style={{ color: "#999" }} />
    //     </div>
    //     <span>{record.fullName}</span>
    //   </div>
    // ),
  },
  {
    title: "Phone",
    dataIndex: "phone",
    key: "phone",
    // sorter: true,
    hideInSearch: true,
  },
  {
    title: "Email",
    dataIndex: "email",
    key: "email",
    // sorter: true,
    hideInSearch: true,
  },
  {
    title: "Address",
    dataIndex: "address",
    key: "address",
    // sorter: true,
    hideInSearch: true,
    ellipsis: true,
  },
  {
    title: "Subscription Status",
    dataIndex: "subscriptionStatus",
    key: "subscriptionStatus",
    sorter: true,
    hideInSearch: true,
    render: (_, record) => {
      const status = record.subscriptionStatus
        ? SubscriptionStatusDisplay[
        record.subscriptionStatus as keyof typeof SubscriptionStatusDisplay
        ]
        : undefined;

      if (!status) {
        return <Badge color="default" text="-" />;
      }

      return <Badge color={status.color} text={status.text} />;
    },
  },
  {
    title: "Subscription Type",
    dataIndex: "subscriptionType",
    key: "subscriptionType",
    sorter: true,
    hideInSearch: true,
    render: (_, record) => {
      const membership = record.memberships?.find((m) => m.vaad?.id === vaadId);
      const type = membership?.subscriptionType
        ? SubscriptionTypeDisplay[membership.subscriptionType]
        : undefined;

      if (!type) {
        return <Badge color="default" text="-" />;
      }

      return (
        <Tag
          color={type.color === "green" ? "green" : "orange"}
          style={{
            borderRadius: 4,
            border: `1px solid ${type.color === "green" ? "#b7eb8f" : "#ffd591"}`,
            background: type.color === "green" ? "#f6ffed" : "#fff7e6",
            color: type.color === "green" ? "#389e0d" : "#d46b08",
          }}
        >
          {type.text}
        </Tag>
      );
    },
  },
];

function ToolBar({
  onCreate,
  vaadId,
  onSuccess,
  onInvite,
}: {
  onCreate: () => void;
  vaadId: string;
  onSuccess: () => void;
  onInvite: () => void;
}) {
  const [selectUserOpen, setSelectUserOpen] = useState(false);
  const { id } = useParams();
  const { download: exportMembers, loading: exportLoading } =
    useUsersExportDownload();

  const dropdownItems: MenuProps["items"] = [
    {
      key: "existing",
      label: "Select existing user",
      onClick: () => setSelectUserOpen(true),
    },
    {
      key: "invite",
      label: "Invite new user",
      onClick: onInvite,
    },
  ];

  return [
    <Button
      key="create"
      icon={<IconExport />}
      type="primary"
      ghost
      loading={exportLoading}
      onClick={() => {
        if (!vaadId) {
          message.error("Vaad ID is missing");
          return;
        }
        exportMembers({ vaadId });
      }}
    >
      Export Members
    </Button>,
    <Dropdown
      key="addMember"
      menu={{ items: dropdownItems }}
      placement="bottomRight"
      trigger={["click"]}
    >
      <Button type="primary">
        Add Member
        <DownOutlined />
      </Button>
    </Dropdown>,
    <SelectExistingUserModal
      key="SelectExistingUserModal"
      open={selectUserOpen}
      onClose={() => setSelectUserOpen(false)}
      vaadId={vaadId}
      onSuccess={() => {
        setSelectUserOpen(false);
        onSuccess();
      }}
    />,
  ];
}

export default function MembersTab({
  activeTab,
}: MembersTabProps): React.ReactNode | null {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(() => Date.now());
  const { t, i18n } = useTranslation();
  const usersGet = useUsersTableGet();
  const [createOpen, setCreateOpen] = useState(false);
  const [EditOpen, setEditOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  useEffect(() => {
    if (activeTab === "members") {
      setTableKey(Date.now());
    }
  }, [activeTab]);

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number | undefined;
      current?: number | undefined;
      keyword?: string | undefined;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<UserTableRow>>> => {
    const newParams = queryFilterParams({
      page: current ? `${current}` : "1",
      pageSize: pageSize ? `${pageSize}` : "10",
      ...args,
      ...getSorterParams(sorter),
    });

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });
    return usersGet
      .fetch({
        ...newParams,
        vaadId: id,
        orderBy: "DESC",
        orderByColumn: "createdAt",
        role: undefined,
        roles: ["student"],
      })
      .then((response) => {
        if (response) {
          const { data, total } = response;

          return {
            data: (data || []).map((item) => ({
              ...item,
              key: item.id,
            })),
            success: true,
            total,
          };
        }

        return { data: [], success: false, total: 0 };
      });
  };
  return (
    <>
      <Table<UserTableRow>
        key={`${tableKey}-${i18n.language}`}
        resetKey={() => setTableKey(Date.now())}
        // className={clsx("transparent")}
        className="tableCustomPadding"
        headerTitle={t("pages.users.tableName")}
        toolBarRender={() => [
          <ToolBar
            key="toolbar"
            onCreate={() => setCreateOpen(true)}
            vaadId={id!}
            onSuccess={() => setTableKey(Date.now())}
            onInvite={() => setInviteOpen(true)}
          />,
        ]}
        request={tableRequest}
        columns={columns(id)}
        onRow={(record) => ({
          onClick: () =>
            navigate(`/users/${record.id}/view`, {
              state: { from: location.pathname + location.search },
            }),
          style: { cursor: "pointer" },
        })}
        search={{
          style: {
            display: "none",
          },
        }}
        options={{
          search: {
            name: "search",
            style: { width: 280 },
          },
        }}
        columnsState={{
          persistenceKey: "pro-table-users",
          persistenceType: "localStorage",
        }}
      />
      <CreateModal
        open={inviteOpen}
        mode="create"
        onClose={() => setInviteOpen(false)}
        setTableKey={() => setTableKey(Date.now())}
        vaadId={id} // ← передаём vaadId
      />
      {/* <EditModal
        open={EditOpen}
        mode="create"
        onClose={() => setEditOpen(false)}
        setTableKey={() => setTableKey(Date.now())}
      /> */}
    </>
  );
}
